import { create } from 'zustand'
import { apiClient, ApiError } from '../lib/api'

export interface ClipManifestEntry {
  verse: number
  url: string
  duration_ms: number
}

export interface VoiceInfo {
  id: string
  name: string
  gender: string
  description: string
  sample_url: string | null
}

type PlayerView = 'hidden' | 'mini' | 'fullscreen'

type PlayerStatus =
  | 'idle'
  | 'loading'
  | 'synthesizing'
  | 'ready'
  | 'playing'
  | 'paused'
  | 'error'

interface AudioBibleState {
  // What we're playing
  bookId: string | null
  chapter: number | null
  translation: string
  voiceId: string

  // Manifest (clips ordered by verse, with cumulative start times)
  clips: ClipManifestEntry[]
  cumulativeStarts: number[] // seconds
  totalDurationSec: number
  progress: { done: number; total: number }

  // Playback state
  status: PlayerStatus
  currentVerse: number | null
  currentPositionSec: number
  speed: number
  autoNext: boolean
  errorMessage: string | null

  // UI state
  view: PlayerView
  voicePickerOpen: boolean

  // Voices
  voices: VoiceInfo[]

  // Actions
  loadVoices: () => Promise<void>
  play: (book: string, chapter: number, translation: string, options?: { voiceId?: string; startVerse?: number }) => Promise<void>
  pause: () => void
  resume: () => void
  seek: (sec: number) => void
  jumpToVerse: (verseNum: number) => void
  setSpeed: (speed: number) => void
  setVoice: (voiceId: string) => Promise<void>
  setCurrentVerse: (verseNum: number | null) => void
  setCurrentPosition: (sec: number) => void
  setStatus: (status: PlayerStatus) => void
  expand: () => void
  collapse: () => void
  dismiss: () => void
  openVoicePicker: () => void
  closeVoicePicker: () => void
  toggleAutoNext: () => void
  _pollManifest: () => Promise<void>
  _cancelPolling: () => void
}

const DEFAULT_VOICE = 'gemini:Aoede'
const SPEED_CYCLE = [0.75, 1, 1.25, 1.5]

function computeCumulative(clips: ClipManifestEntry[]): { starts: number[]; total: number } {
  const starts: number[] = []
  let running = 0
  for (const c of clips) {
    starts.push(running)
    running += c.duration_ms / 1000
  }
  return { starts, total: running }
}

let pollTimer: ReturnType<typeof setTimeout> | null = null

export const useAudioBibleStore = create<AudioBibleState>((set, get) => ({
  bookId: null,
  chapter: null,
  translation: 'ESV',
  voiceId: DEFAULT_VOICE,

  clips: [],
  cumulativeStarts: [],
  totalDurationSec: 0,
  progress: { done: 0, total: 0 },

  status: 'idle',
  currentVerse: null,
  currentPositionSec: 0,
  speed: 1,
  autoNext: true,
  errorMessage: null,

  view: 'hidden',
  voicePickerOpen: false,

  voices: [],

  loadVoices: async () => {
    try {
      const voices = await apiClient.get<VoiceInfo[]>('bible/audio/voices/')
      set({ voices })
    } catch {
      // swallow — voices list is optional
    }
  },

  play: async (book, chapter, translation, options) => {
    const voiceId = options?.voiceId || get().voiceId
    const prev = get()
    get()._cancelPolling()

    // Reset for the new chapter
    set({
      bookId: book,
      chapter,
      translation,
      voiceId,
      clips: [],
      cumulativeStarts: [],
      totalDurationSec: 0,
      progress: { done: 0, total: 0 },
      currentVerse: options?.startVerse ?? null,
      currentPositionSec: 0,
      status: 'loading',
      errorMessage: null,
      view: prev.view === 'fullscreen' ? 'fullscreen' : 'mini',
    })

    await get()._pollManifest()
  },

  _pollManifest: async () => {
    const { bookId, chapter, translation, voiceId } = get()
    if (!bookId || !chapter) return

    try {
      const url =
        `bible/audio/manifest/?book=${encodeURIComponent(bookId)}` +
        `&chapter=${chapter}&translation=${encodeURIComponent(translation)}` +
        `&voice=${encodeURIComponent(voiceId)}`
      const resp = await fetch(`/api/v1/${url}`, { credentials: 'same-origin' })
      if (!resp.ok && resp.status !== 202 && resp.status !== 200) {
        set({ status: 'error', errorMessage: 'Could not load audio' })
        return
      }
      const data = await resp.json()

      if (data.state === 'ready') {
        const { starts, total } = computeCumulative(data.clips as ClipManifestEntry[])
        set({
          clips: data.clips,
          cumulativeStarts: starts,
          totalDurationSec: total,
          progress: { done: data.clips.length, total: data.clips.length },
          status: 'ready',
        })
      } else if (data.state === 'partial') {
        // Don't start playback mid-synthesis — the audio-hook reloads clips on
        // every manifest update, which causes repeated-word glitches. Keep
        // progress visible and wait for `ready`.
        set({
          progress: data.progress,
          status: 'synthesizing',
        })
        pollTimer = setTimeout(() => {
          void get()._pollManifest()
        }, 2500)
      } else {
        // synthesizing with no clips yet
        set({ status: 'synthesizing', progress: data.progress ?? { done: 0, total: 0 } })
        pollTimer = setTimeout(() => {
          void get()._pollManifest()
        }, 2500)
      }
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0
      set({
        status: 'error',
        errorMessage: status === 401 ? 'Please sign in' : 'Audio unavailable',
      })
    }
  },

  _cancelPolling: () => {
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  },

  pause: () => {
    set({ status: 'paused' })
  },

  resume: () => {
    set({ status: 'playing' })
  },

  seek: (sec) => {
    const { cumulativeStarts, totalDurationSec } = get()
    const clamped = Math.max(0, Math.min(sec, totalDurationSec))
    // Find which verse clamped time falls into
    let verseIdx = 0
    for (let i = 0; i < cumulativeStarts.length; i++) {
      if (cumulativeStarts[i] <= clamped) verseIdx = i
      else break
    }
    const { clips } = get()
    set({
      currentPositionSec: clamped,
      currentVerse: clips[verseIdx]?.verse ?? null,
    })
  },

  jumpToVerse: (verseNum) => {
    const { clips, cumulativeStarts } = get()
    const idx = clips.findIndex((c) => c.verse === verseNum)
    if (idx < 0) return
    set({
      currentVerse: verseNum,
      currentPositionSec: cumulativeStarts[idx],
    })
  },

  setSpeed: (speed) => {
    if (!SPEED_CYCLE.includes(speed)) return
    set({ speed })
  },

  setVoice: async (voiceId) => {
    const { bookId, chapter, translation } = get()
    set({ voiceId })
    if (bookId && chapter) {
      await get().play(bookId, chapter, translation, { voiceId })
    }
  },

  setCurrentVerse: (verseNum) => set({ currentVerse: verseNum }),
  setCurrentPosition: (sec) => set({ currentPositionSec: sec }),
  setStatus: (status) => set({ status }),

  expand: () => set({ view: 'fullscreen' }),
  collapse: () => {
    const { status } = get()
    set({ view: status === 'idle' ? 'hidden' : 'mini' })
  },
  dismiss: () => {
    get()._cancelPolling()
    set({
      view: 'hidden',
      status: 'idle',
      bookId: null,
      chapter: null,
      clips: [],
      cumulativeStarts: [],
      totalDurationSec: 0,
      progress: { done: 0, total: 0 },
      currentVerse: null,
      currentPositionSec: 0,
      errorMessage: null,
    })
  },

  openVoicePicker: () => set({ voicePickerOpen: true }),
  closeVoicePicker: () => set({ voicePickerOpen: false }),
  toggleAutoNext: () => set((s) => ({ autoNext: !s.autoNext })),
}))
